import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from 'recharts';
import { FC, ReactNode, memo, useMemo, useRef } from 'react';

import { PunchupSpinner } from 'features/common/components/Punchup/primitives/feedback/PunchupSpinner';
import { computeYAxisWidth } from 'features/common/utils/chartAxis';
import { CHART_PRIMARY_COLOR } from 'features/common/utils/chartColors';
import { common } from 'theme';
import styles from './PunchupAreaChart.module.css';

export interface PunchupAreaChartProps {
	/** Pre-processed data array. Each entry must include the x-axis key and the data key. */
	data: Record<string, unknown>[];
	/** Key in each data entry to use for the Y axis (e.g. 'payout', 'count'). */
	dataKey: string;
	/** Key in each data entry to use for the X axis labels. Defaults to 'date'. */
	xAxisKey?: string;
	/** Card header title (e.g. "Net Sales"). */
	title?: string;
	/** Icon rendered left of the title. Accepts any ReactNode sized to ~18px. */
	icon?: ReactNode;
	/**
	 * Aggregate label shown below the title (e.g. "Total Payout").
	 * Displayed as muted secondary text.
	 */
	totalLabel?: string;
	/**
	 * Aggregate value shown below totalLabel (e.g. "$4,320").
	 * Displayed at display scale (26px/700) for immediate visual prominence.
	 */
	totalValue?: string;
	/**
	 * Slot for controls rendered on the right side of the card header.
	 * Intended for TimelineControls (period/zoom selectors) or date range pickers.
	 * The primitive does not manage timeline state — the caller owns that and
	 * passes pre-filtered data.
	 */
	headerRight?: ReactNode;
	/** Formats X-axis tick labels (e.g. abbreviated dates). Receives the raw data value. */
	xAxisFormatter?: (value: unknown) => string;
	/** Formats Y-axis tick labels (e.g. adds '$' prefix). */
	yAxisFormatter?: (value: number) => string;
	/**
	 * Custom Recharts Tooltip content component.
	 * When omitted, Recharts renders its default tooltip.
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	customTooltip?: FC<TooltipProps<any, any>>;
	/** Chart area height in px. Defaults to 280. */
	height?: number;
	/** When true, renders a fixed-height loading shell instead of the chart — no layout shift. */
	isLoading?: boolean;
	/**
	 * Optional help icon anchored to the bottom-right corner of the card.
	 * Pass a <PunchupHelpButton> — the card already has position:relative.
	 */
	helpButton?: ReactNode;
}

const PunchupAreaChartBase: FC<PunchupAreaChartProps> = ({
	data,
	dataKey,
	xAxisKey = 'date',
	title,
	icon,
	totalLabel,
	totalValue,
	headerRight,
	xAxisFormatter,
	yAxisFormatter,
	customTooltip,
	height = 280,
	isLoading,
	helpButton,
}) => {
	// Stable per-instance ID for the SVG linearGradient — must be unique across concurrent chart instances
	const gradientId = useRef(`paf-${Math.random().toString(36).slice(2, 8)}`).current;
	const hasHeader = !!(title || icon || totalLabel || totalValue || headerRight);

	// Size the Y-axis gutter to the longest formatted tick so labels never clip.
	const yAxisWidth = useMemo(
		() =>
			computeYAxisWidth(
				data.map((d) => Number(d[dataKey]) || 0),
				yAxisFormatter
			),
		[data, dataKey, yAxisFormatter]
	);

	return (
		<div className={styles.card}>
			{hasHeader && (
				<div className={styles.cardHeader}>
					<div className={styles.cardHeaderLeft}>
						{(title || icon) && (
							<div className={styles.titleRow}>
								{icon && <span className={styles.iconSlot}>{icon}</span>}
								{title && <span className={styles.title}>{title}</span>}
							</div>
						)}
						{(totalLabel || totalValue) && (
							<div className={styles.totalRow}>
								{totalLabel && <span className={styles.totalLabel}>{totalLabel}</span>}
								{totalValue && <span className={styles.totalValue}>{totalValue}</span>}
							</div>
						)}
					</div>
					{headerRight && <div className={styles.cardHeaderRight}>{headerRight}</div>}
				</div>
			)}
			{isLoading ? (
				<div className={styles.loadingShell} style={{ height }}>
					<PunchupSpinner size={32} />
				</div>
			) : (
				<div className={styles.chartArea} style={{ height }}>
					<ResponsiveContainer width="100%" height="100%">
						<AreaChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
							<defs>
								<linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
									<stop offset="5%" stopColor={CHART_PRIMARY_COLOR} stopOpacity={0.3} />
									<stop offset="95%" stopColor={CHART_PRIMARY_COLOR} stopOpacity={0} />
								</linearGradient>
							</defs>
							<CartesianGrid strokeDasharray="3 3" vertical={false} stroke={common.colors.grey19} />
							<XAxis
								dataKey={xAxisKey}
								tickFormatter={xAxisFormatter}
								tick={{
									fill: common.colors.secondaryBlue,
									fontSize: 12,
									fontFamily: common.fonts.primary,
								}}
								tickLine={false}
								axisLine={false}
							/>
							<YAxis
								tickFormatter={yAxisFormatter}
								tick={{
									fill: common.colors.secondaryBlue,
									fontSize: 12,
									fontFamily: common.fonts.primary,
								}}
								tickLine={false}
								axisLine={false}
								width={yAxisWidth}
							/>
							<Tooltip content={customTooltip} />
							<Area
								type="monotone"
								dataKey={dataKey}
								stroke={CHART_PRIMARY_COLOR}
								strokeWidth={2}
								fill={`url(#${gradientId})`}
								dot={false}
								activeDot={{ r: 4, fill: CHART_PRIMARY_COLOR }}
								isAnimationActive={false}
							/>
						</AreaChart>
					</ResponsiveContainer>
				</div>
			)}
			{helpButton}
		</div>
	);
};

export const PunchupAreaChart = memo(PunchupAreaChartBase);
PunchupAreaChart.displayName = 'PunchupAreaChart';
